import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Loader2, Search, Paperclip, MailPlus, CheckCircle2 } from 'lucide-react';

const fmtDt = (iso) => iso
  ? new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
  : '';

export const AddMailDialog = ({ open, onClose, onDone, mode = 'proposal' }) => {
  const isDisc = mode === 'discussion';
  const [q, setQ] = useState('');
  const [msgs, setMsgs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (!open) return;
    setQ('');
    setMsgs(null);
    setLoading(true);
    api.getMailBrowser('', isDisc ? 'discussion' : 'proposal')
      .then(r => setMsgs(r.data.messages))
      .catch(err => toast.error(err.response?.data?.detail || 'Postfach konnte nicht geladen werden'))
      .finally(() => setLoading(false));
  }, [open, isDisc]);

  useEffect(() => {
    if (!open || msgs === null) return;
    const t = setTimeout(() => {
      setLoading(true);
      api.getMailBrowser(q, isDisc ? 'discussion' : 'proposal')
        .then(r => setMsgs(r.data.messages))
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const add = async (m) => {
    setBusy(m.message_id);
    try {
      if (isDisc) {
        const r = await api.addMailAsDiscussion(m.message_id);
        toast.success(r.data.reopened
          ? 'Discussion wieder geöffnet — KI-Antwort steht bereit'
          : 'In Broker Discussions aufgenommen — KI-Antwort steht bereit');
      } else {
        const r = await api.addMailAsProposal(m.message_id);
        toast.success(`Als Deal-Proposal aufgenommen${r.data.company ? ` — ${r.data.company}` : ''} · KI-Extraktion & 72h-Task angelegt`);
      }
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Aufnahme fehlgeschlagen');
    }
    setBusy(null);
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="add-mail-dialog" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <MailPlus className="w-5 h-5 text-sky-400" /> {isDisc ? 'E-Mail als Broker-Discussion aufnehmen' : 'E-Mail als Deal aufnehmen'}
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground -mt-1">
          {isDisc
            ? 'Dein Posteingang — flagge eine Broker-Mail, die du hier mit KI-vorbereiteter Antwort beantworten willst.'
            : 'Dein Posteingang — für Broker-Mails, die die KI nicht erkannt hat. Die Aufnahme läuft durch dieselbe Pipeline wie der automatische Scan (KI-Extraktion, Broker-Verknüpfung, Auto-Bestätigung, 72h-Task).'}
        </p>
        <div className="relative">
          <Search className="w-4 h-4 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Absender oder Betreff suchen…"
            className="pl-8 text-sm h-9" data-testid="add-mail-search-input" />
        </div>
        {loading && msgs === null ? (
          <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="space-y-1.5 max-h-[52vh] overflow-y-auto pr-1" data-testid="add-mail-list">
            {(msgs || []).length === 0 && !loading && (
              <p className="text-xs text-muted-foreground text-center py-6">Keine E-Mails gefunden.</p>
            )}
            {(msgs || []).map((m, i) => (
              <div key={m.message_id}
                className={`border border-border rounded-sm p-2.5 flex items-center gap-3 ${m.own_domain ? 'opacity-45' : ''}`}
                data-testid={`add-mail-row-${i}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-foreground font-medium truncate flex items-center gap-1.5">
                    {m.has_attachments && <Paperclip className="w-3 h-3 text-sky-400 shrink-0" />}
                    {m.subject || '(kein Betreff)'}
                  </p>
                  <p className="text-[11px] text-muted-foreground truncate">
                    {m.from_name || m.from_email} · {m.from_email} · {fmtDt(m.ts)}
                  </p>
                  {m.preview && <p className="text-[10px] text-muted-foreground/70 truncate mt-0.5">{m.preview}</p>}
                </div>
                {m.already_added ? (
                  <span className="text-[10px] font-mono border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 rounded px-1.5 py-0.5 flex items-center gap-1 shrink-0"
                    data-testid={`add-mail-already-${i}`}>
                    <CheckCircle2 className="w-3 h-3" /> erfasst
                  </span>
                ) : m.own_domain ? (
                  <span className="text-[10px] text-muted-foreground shrink-0">eigene Mail</span>
                ) : (
                  <Button size="sm" onClick={() => add(m)} disabled={!!busy} data-testid={`add-mail-btn-${i}`}
                    className="bg-sky-600 hover:bg-sky-500 text-white rounded-sm text-[11px] h-7 px-3 shrink-0">
                    {busy === m.message_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Aufnehmen'}
                  </Button>
                )}
              </div>
            ))}
            {loading && msgs !== null && (
              <div className="flex justify-center py-2"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
